import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion, faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import { FeedbackFeature, FeedbackRating, submitFeatureFeedback } from '../services/FeatureFeedbackService';

const MAX_COMMENT_LENGTH = 2000;

/**
 * A small "was this useful?" widget meant to live at the bottom of a FloatingWindow.
 *
 * The rating buttons and the comment box are always shown, and nothing is sent until
 * Send is pressed. Send stays disabled until there is something worth sending: either
 * a comment, or a "Not sure" rating (which is informative on its own, unlike Yes/No
 * with no context). The form resets after a successful send, so a user can submit as
 * many entries as they like.
 *
 * @param props.feature which part of the site is being rated
 * @param props.context optional extra information stored alongside the feedback (e.g. the layer)
 * @param props.enabled feedback requires a logged in user, the widget hides itself when false
 */
export default function FeatureFeedback(props: {
    feature: FeedbackFeature,
    context?: { [key: string]: unknown },
    enabled?: boolean
}) {
    const [rating, setRating] = useState<FeedbackRating | null>(null);
    const [comment, setComment] = useState<string>('');
    const [sending, setSending] = useState<boolean>(false);

    if (!props.enabled) {
        return null;
    }

    const reset = () => {
        setRating(null);
        setComment('');
        setSending(false);
    };

    const send = async (value: FeedbackRating, text?: string) => {
        setSending(true);
        const ok = await submitFeatureFeedback({
            feature: props.feature,
            rating: value,
            comment: text,
            context: props.context
        });
        setSending(false);

        if (ok) {
            toast.success('Thank you for your feedback');
        } else {
            toast.error('Failed to send your feedback');
        }
        return ok;
    };

    const onSend = async () => {
        if (await send(rating!, comment.trim())) {
            reset();
        }
    };

    const ratingButton = (value: FeedbackRating, icon: IconDefinition, label: string, testid: string) =>
        <Tooltip title={label} placement="bottom" arrow disableInteractive>
            <button
                type="button"
                data-testid={testid}
                aria-label={label}
                className={`btn btn-sm ${rating === value ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1`}
                disabled={sending}
                onClick={() => setRating(value)}
            >
                <FontAwesomeIcon icon={icon} />
            </button>
        </Tooltip>;

    // "Not sure" always has something to send (the rating itself), Yes/No tell us
    // nothing without a comment. A comment with no rating at all is not sendable -
    // the entry is stored against a rating
    const canSend = !sending && rating !== null
        && (rating === FeedbackRating.UNSURE || comment.trim().length > 0);

    return <>
        <div className='d-flex flex-column p-2 fs-8' data-testid='feature-feedback-panel'>
            <span className='mb-2'>Was this useful?</span>
            <div className='d-flex flex-row gap-2'>
                {ratingButton(FeedbackRating.YES, faThumbsUp, 'Yes', 'feature-feedback-yes')}
                {ratingButton(FeedbackRating.NO, faThumbsDown, 'No', 'feature-feedback-no')}
                {ratingButton(FeedbackRating.UNSURE, faQuestion, 'Not sure', 'feature-feedback-unsure')}
            </div>
            <label htmlFor='feature-feedback-comment' className='mt-3 mb-1'>
                {rating === FeedbackRating.UNSURE ? 'What were you looking for?' : 'Anything to add? (optional)'}
            </label>
            <textarea
                id='feature-feedback-comment'
                data-testid='feature-feedback-comment'
                className='form-control fs-8'
                rows={3}
                maxLength={MAX_COMMENT_LENGTH}
                value={comment}
                disabled={sending}
                onChange={(e) => setComment(e.target.value)}
            />
            <button
                type='button'
                data-testid='feature-feedback-send'
                className='btn btn-sm btn-primary mt-2 ms-auto'
                disabled={!canSend}
                onClick={onSend}
            >
                Send
            </button>
        </div>
    </>;
}
